/**
 * Ultimas noticias da home do Cabal Next (referencia do dono: "Latest News" do Cabal SEA, no nosso tema azul).
 * Carrossel quadrado com moldura (setas, losangos, arrastar) + lista com abas por categoria.
 *
 * Dados e categorias: newsData.ts (dividido com as paginas /news e /news/:id).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import {
  CATEGORY_LABEL,
  NEWS_ASSET,
  NEWS_TABS,
  newsPlainTitle,
  useNewsCards,
  type NewsCard,
  type NewsTab,
} from './newsData';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';
import './news.css';

const AUTOPLAY_MS = 5500;
const LIST_SIZE = 5;
const SLIDES = 6;

function Carousel({ slides, onOpen }: { slides: NewsCard[]; onOpen: (c: NewsCard) => void }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const drag = useRef<{ x: number } | null>(null);
  const count = slides.length;

  const go = useCallback((to: number) => setIndex(((to % count) + count) % count), [count]);

  // passa sozinho; para com o mouse em cima/foco, aba escondida e "reduzir movimento"
  useEffect(() => {
    if (paused || count < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const timer = window.setInterval(() => {
      if (!document.hidden) setIndex((i) => (i + 1) % count);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(timer);
  }, [paused, count]);

  const onPointerDown = (e: ReactPointerEvent) => {
    drag.current = { x: e.clientX };
  };
  const onPointerUp = (e: ReactPointerEvent) => {
    const start = drag.current;
    drag.current = null;
    if (!start) return;
    const dx = e.clientX - start.x;
    if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
    else onOpen(slides[index]);
  };

  return (
    <div
      className="nxn-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <div className="nxn-frame">
        <div
          className="nxn-track"
          style={{ transform: `translateX(-${index * 100}%)` }}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={() => (drag.current = null)}
        >
          {slides.map((s, i) => (
            <figure key={s.slug ?? `fb-${i}`} className="nxn-slide" aria-hidden={i !== index}>
              <img
                src={newsImg(s.image, 720)}
                onError={newsImgFallback(s.image)}
                alt={s.title}
                draggable={false}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
              <figcaption>
                <span className={`nxn-chip nxn-chip--${s.category}`}>{CATEGORY_LABEL[s.category]}</span>
                <strong>{newsPlainTitle(s.title)}</strong>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <button type="button" className="nxn-arrow nxn-arrow--prev" onClick={() => go(index - 1)} aria-label="Anterior">
        <ChevronsLeft size={22} />
      </button>
      <button type="button" className="nxn-arrow nxn-arrow--next" onClick={() => go(index + 1)} aria-label="Próxima">
        <ChevronsRight size={22} />
      </button>
      <div className="nxn-dots" role="tablist">
        {slides.map((s, i) => (
          <button
            key={s.slug ?? `dot-${i}`}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Notícia ${i + 1}`}
            className={i === index ? 'is-active' : undefined}
            onClick={() => go(i)}
          />
        ))}
      </div>
    </div>
  );
}

export function NewsSection() {
  const navigate = useNavigate();
  const { cards, loading } = useNewsCards();
  const [tab, setTab] = useState<NewsTab>('todas');
  const open = useCallback((c: NewsCard) => navigate(c.slug ? `/news/${c.slug}` : '/news'), [navigate]);

  const slides = useMemo(() => cards.slice(0, SLIDES), [cards]);
  const list = useMemo(
    () => (tab === 'todas' ? cards : cards.filter((c) => c.category === tab)).slice(0, LIST_SIZE),
    [cards, tab]
  );

  return (
    <section id="noticias" className="nxn" style={{ backgroundImage: `url(${NEWS_ASSET}/news-bg.webp)` }}>
      <div className="nx-container">
        <header className="nxn-head" data-reveal>
          <h2 className="nxn-title">Últimas notícias</h2>
          <p className="nxn-subtitle">Atualizações, patch notes e eventos do Cabal Next em primeira mão.</p>
        </header>

        <div className="nxn-grid">
          <div data-reveal style={{ '--d': '0.1s' } as CSSProperties}>
            {slides.length ? (
              <Carousel slides={slides} onOpen={open} />
            ) : (
              <div className="nxn-carousel">
                {loading ? (
                  <div className="nxn-frame nx-skeleton" />
                ) : (
                  <div className="nxn-frame nxn-frame--empty">Nenhuma notícia publicada ainda.</div>
                )}
              </div>
            )}
          </div>

          <div className="nxn-side" data-reveal style={{ '--d': '0.2s' } as CSSProperties}>
            <div className="nxn-tabs" role="tablist">
              {NEWS_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={tab === t.id ? 'is-active' : undefined}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <ul className="nxn-list">
              {list.map((n, i) => (
                <li key={n.slug ?? `item-${i}`}>
                  <button type="button" onClick={() => open(n)}>
                    <strong>{n.title}</strong>
                    <p>{n.text}</p>
                    <span className="nxn-meta">
                      <span className="nxn-chip nxn-chip--date">{n.date}</span>
                      <span className={`nxn-chip nxn-chip--${n.category}`}>{CATEGORY_LABEL[n.category]}</span>
                    </span>
                  </button>
                </li>
              ))}
              {loading &&
                [0, 1, 2, 3].map((i) => (
                  <li key={`sk-${i}`} className="nxn-skeleton-row">
                    <span className="nx-skeleton" />
                    <span className="nx-skeleton" />
                  </li>
                ))}
              {!loading && !list.length && <li className="nxn-empty">Nenhuma notícia nesta categoria ainda.</li>}
            </ul>
            <Link className="nxn-all" to="/news">
              Ver todas
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
